import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

const MAX_POLLS = 8;

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  const fmt = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n || 0);

  return (
    <div className="container-x py-24 max-w-2xl mx-auto text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#6FAA8A]" />
          <h1 className="display text-2xl font-semibold mt-6">Betaling verifiëren…</h1>
          <p className="text-[#A8B3AD] mt-2">Een moment, we bevestigen je betaling bij Stripe.</p>
        </div>
      )}

      {state === "paid" && (
        <div data-testid="payment-paid">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#1F3A2E] grid place-items-center">
            <CheckCircle2 className="w-10 h-10 text-[#FDFBF7]" />
          </div>
          <h1 className="display text-3xl md:text-4xl font-semibold mt-6">Betaling geslaagd</h1>
          <p className="text-[#A8B3AD] mt-3">
            {info?.plan === "deposit" ? "Aanbetaling (50%) ontvangen" : "Volledig bedrag ontvangen"}: <b>{fmt(info?.amount)}</b>
          </p>
          {info?.quote_number && <p className="text-[#A8B3AD] mt-1">Offertenummer <b>{info.quote_number}</b></p>}
          <div className="mt-8 flex gap-3 justify-center">
            <button className="btn-primary" onClick={() => nav("/account")} data-testid="payment-goto-account">Naar mijn account</button>
            <button className="btn-outline" onClick={() => nav("/")} data-testid="payment-back-home"><ArrowLeft className="w-4 h-4" /> Home</button>
          </div>
        </div>
      )}

      {state === "pending" && (
        <div data-testid="payment-pending">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#D97742]" />
          <h1 className="display text-2xl font-semibold mt-6">Betaling wordt verwerkt</h1>
          <p className="text-[#A8B3AD] mt-2">Dit kan een moment duren. Je ontvangt een bevestiging zodra de betaling is verwerkt.</p>
          <button className="btn-outline mt-8" onClick={() => nav("/")} data-testid="payment-back-home-2"><ArrowLeft className="w-4 h-4" /> Home</button>
        </div>
      )}

      {state === "error" && (
        <div data-testid="payment-error">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#5A2222] grid place-items-center">
            <XCircle className="w-10 h-10 text-[#FDFBF7]" />
          </div>
          <h1 className="display text-2xl font-semibold mt-6">Betaling niet gelukt</h1>
          <p className="text-[#A8B3AD] mt-2">Er is iets misgegaan of de betaling is geannuleerd. Probeer het opnieuw vanuit je offerte.</p>
          <button className="btn-outline mt-8" onClick={() => nav("/cart")} data-testid="payment-retry"><ArrowLeft className="w-4 h-4" /> Terug naar winkelmandje</button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancelPage() {
  const nav = useNavigate();
  return (
    <div className="container-x py-24 max-w-2xl mx-auto text-center" data-testid="payment-cancel-page">
      <div className="w-20 h-20 mx-auto rounded-full bg-[#5A2222] grid place-items-center">
        <XCircle className="w-10 h-10 text-[#FDFBF7]" />
      </div>
      <h1 className="display text-3xl font-semibold mt-6">Betaling geannuleerd</h1>
      <p className="text-[#A8B3AD] mt-3">Je betaling is geannuleerd. Je offerte blijft gewoon staan, je kunt het later opnieuw proberen.</p>
      <button className="btn-primary mt-8" onClick={() => nav("/account")} data-testid="cancel-goto-account">Naar mijn account</button>
    </div>
  );
}
